import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthCallback from "./components/AuthCallback";
import SupportChat from "./components/SupportChat";
import CookieBanner from "./components/CookieBanner";
import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import PaymentPaypalReturn from "./pages/PaymentPaypalReturn";
import ResetPassword from "./pages/ResetPassword";
import AdminPage from "./pages/AdminPage";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Privacy from "./pages/Privacy";
import CreazioneSitiWebNovara from "./pages/CreazioneSitiWebNovara";
import WebDesignNovara from "./pages/WebDesignNovara";
import SeoNovara from "./pages/SeoNovara";
import GestioneSitiWeb from "./pages/GestioneSitiWeb";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF8F5]">
        <span className="mono-label text-neutral-500">Caricamento…</span>
      </div>
    );
  if (user === false) return <Navigate to="/accedi" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const excluded = ["/admin", "/dashboard", "/accedi", "/payment", "/reset-password"];
    if (excluded.some((p) => pathname.startsWith(p))) return;
    if (localStorage.getItem("lumora_cookie_consent") !== "accepted") return;
    let vid = localStorage.getItem("lumora_vid");
    if (!vid) {
      vid = crypto.randomUUID();
      localStorage.setItem("lumora_vid", vid);
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: pathname, vid, ref: document.referrer }),
    }).catch(() => {});
  }, [pathname]);
  return null;
}

function AppShell() {
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  // Emergent Google OAuth: process session_id FIRST, before any route/auth check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <div className="grain bg-[#FAF8F5] text-[#111111] min-h-screen">
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/accedi" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/dashboard"
          element={
            <Protected>
              <Dashboard />
            </Protected>
          }
        />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/creazione-siti-web-novara" element={<CreazioneSitiWebNovara />} />
        <Route path="/web-design-novara" element={<WebDesignNovara />} />
        <Route path="/seo-novara" element={<SeoNovara />} />
        <Route path="/gestione-siti-web" element={<GestioneSitiWeb />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/paypal/return" element={<PaymentPaypalReturn />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
      {!["/accedi", "/admin", "/dashboard", "/payment"].some((p) => location.pathname.startsWith(p)) && (
        <SupportChat />
      )}
      {!["/accedi", "/admin", "/dashboard", "/payment"].some((p) => location.pathname.startsWith(p)) && (
        <CookieBanner />
      )}
      <Toaster position="bottom-right" toastOptions={{ style: { background: "#111111", color: "#FAF8F5", border: "none" } }} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
